import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, Shield, Calendar, CheckCircle, Eye, Users, Mail, Award, BookOpen, FileText, Clock } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-300 rounded-lg mb-4 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/10" : "bg-white";
  const borderColor = type === "important" ? "border-[#ab3d22]/30" : "border-gray-300";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6 shadow-sm`}>
      {children}
    </div>
  );
};

const EditorialStandardsPage = () => {
  const [openSections, setOpenSections] = useState({
    ourPromise: true,
    editorialIndependence: false,
    sponsoredContent: false,
    whatWeDo: false,
    whatWeDontDo: false,
    aboutRatings: false,
    corrections: false,
    contact: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />
      
      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#d6dbd8]"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-slate-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-zinc-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:min-h-[200px] w-full">
            <div className="text-left flex-1 max-w-lg">
              {/* Main Headline */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                Editorial Standards
                {/* Brand Accent Line */}
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-[#ab3d22] rounded-full"></div>
              </h1>
              
              {/* Refined Tagline */}
              <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light">
                Our Commitment to Integrity, Clarity, and Transparency
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: December 8, 2025</span>
          </div>

          {/* Our Promise */}
          <AccordionItem
            title="Our Promise"
            isOpen={openSections.ourPromise}
            onToggle={() => toggleSection('ourPromise')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance was created to bring honesty back to beauty and wellness.
              </p>
              <p className="text-sm text-gray-600">
                Our mission is simple: help you make confident decisions without the noise, pressure, or hidden agendas that dominate the beauty world.
              </p>
              <p className="text-sm text-gray-600">
                Every recommendation on Radiance is shaped by:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Insights surfaced through AI</li>
                <li>• Patterns in real beauty conversations</li>
                <li>• Editorial judgment from humans who understand what shoppers actually care about</li>
              </ul>
              <p className="text-sm text-gray-600">
                Our goal is to make product discovery clearer, smarter, and more trustworthy — not more overwhelming.
              </p>
            </div>
          </AccordionItem>

          {/* Editorial Independence */}
          <AccordionItem
            title="Editorial Independence"
            isOpen={openSections.editorialIndependence}
            onToggle={() => toggleSection('editorialIndependence')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance's editorial process is completely independent from business and advertising decisions.
              </p>
              
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• No brand can buy a positive review.</li>
                <li>• No affiliate partner influences our rankings or recommendations.</li>
                <li>• If a product appears on Radiance, it's because it earned its spot — full stop.</li>
              </ul>

              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700">
                      This independence is at the core of who we are.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Sponsored Content */}
          <AccordionItem
            title="Sponsored Content"
            isOpen={openSections.sponsoredContent}
            onToggle={() => toggleSection('sponsoredContent')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                On rare occasions, we may collaborate with brands or feature sponsored articles.
              </p>
              <p className="text-sm text-gray-600">
                When we do, they will always be:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Clearly labeled ("Sponsored" or "In Partnership With…")</li>
                <li>• Reviewed for accuracy</li>
                <li>• Held to the same editorial standards as everything else on Radiance</li>
              </ul>
              <p className="text-sm text-gray-600 font-medium">
                Sponsored content will never change how we recommend products.
              </p>
            </div>
          </AccordionItem>

          {/* What We Do */}
          <AccordionItem
            title="What We Do"
            isOpen={openSections.whatWeDo}
            onToggle={() => toggleSection('whatWeDo')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance's editorial process blends modern technology with human insight.
              </p>
              <p className="text-sm text-gray-600">
                We:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Identify consistent patterns across beauty discussions and product conversations</li>
                <li>• Highlight what real people repeatedly say about performance, feel, and results</li>
                <li>• Provide clear summaries of who a product is best for — and who it isn't</li>
                <li>• Focus on real-world use, not marketing claims</li>
                <li>• Continuously refine our content as new insights and community feedback emerge</li>
              </ul>
              <p className="text-sm text-gray-600">
                The goal is always the same: to help you find what works, faster and with more confidence.
              </p>
            </div>
          </AccordionItem>

          {/* What We Don't Do */}
          <AccordionItem
            title="What We Don't Do"
            isOpen={openSections.whatWeDontDo}
            onToggle={() => toggleSection('whatWeDontDo')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                To protect your trust, we never:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Accept payment or free products in exchange for positive reviews</li>
                <li>• Allow brands to influence our editorial opinions</li>
                <li>• Recommend anything we wouldn't genuinely stand behind</li>
                <li>• Hide affiliate relationships — we always disclose them</li>
                <li>• Prioritize commissions over honesty</li>
              </ul>
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700">
                      Integrity is the backbone of Radiance.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* About Ratings & Scores */}
          <AccordionItem
            title="About Ratings & Scores"
            isOpen={openSections.aboutRatings}
            onToggle={() => toggleSection('aboutRatings')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance is evolving in phases.
              </p>
              
              <p className="text-sm text-gray-600">
                Right now, some product pages may display temporary placeholder scores or simplified sentiment indicators while we build out our full scoring system.
              </p>
              
              <p className="text-sm text-gray-600">
                As Radiance grows, ratings will increasingly reflect real reviews and experiences from our own community — a future where beauty users shape the platform with their authentic voices.
              </p>

              <p className="text-sm text-gray-600">
                Our goal is to create a rating system grounded in transparency and real user insight, not marketing.
              </p>
            </div>
          </AccordionItem>

          {/* Corrections Policy */}
          <AccordionItem
            title="Corrections Policy"
            isOpen={openSections.corrections}
            onToggle={() => toggleSection('corrections')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                If we get something wrong, we fix it.
              </p>
              
              <p className="text-sm text-gray-600">
                Our corrections process includes:
              </p>
              
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Thoroughly investigating the issue</li>
                <li>• Verifying the correct information</li>
                <li>• Updating the content promptly</li>
                <li>• Clearly noting what changed and why</li>
                <li>• Notifying readers when appropriate</li>
              </ul>

              <CalloutBox>
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700">
                      Accuracy isn't optional — it's part of our responsibility to you.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Contact */}
          <AccordionItem
            title="Contact Us"
            isOpen={openSections.contact}
            onToggle={() => toggleSection('contact')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                If you have questions about our editorial process or want to report an issue, we'd love to hear from you.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-[#ab3d22]" />
                    <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline">
                      hello@meetradiance.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#ab3d22]" />
                    <span className="text-sm font-medium">Response Time:</span>
                    <span className="text-sm text-gray-600">Within 24–48 hours</span>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                Your feedback helps us uphold the standards we promise.
              </p>
            </div>
          </AccordionItem>
        </div>
      </Container>
      
      <Footer />
    </div>
  );
};

export default EditorialStandardsPage;
